import { useEffect, useState, useMemo } from "react";
import OffcanvasTemplate from "../../../components/offcanvasTemplate";
import { fetchApproveForAnotherDate } from "../service";
import DivSpinner from "../../../components/divSpinner";
import { formatDateInJobStatsLog } from "../../../utils";
import RequestDetailsCard from "../../ftmRequests/components/RequestDetailsCard";
import ExtraMaterials from "../../ftmRequests/components/ExtraMaterials";
import CostBreakdown from "../../ftmRequests/components/CostBreakdown";

const ApproveForAnotherDate = ({
    approve_for_another_date_job_id,
    show,
    handleClose
}) => {
    const [loading, setLoading] = useState(true);
    const [anotherDateJob, setAnotherDateJob] = useState(null);

    useEffect(() => {
        if (!approve_for_another_date_job_id) return;

        const fetchData = async () => {
            try {
                setLoading(true);
                const { status, another_date_job } =
                    await fetchApproveForAnotherDate(approve_for_another_date_job_id);

                if (status === "Success") {
                    setAnotherDateJob(another_date_job);
                }
            } catch (error) {
                console.error("ApproveForAnotherDate error:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [approve_for_another_date_job_id]);

    const isApproved = anotherDateJob?.approve_for_another_date_job_ftm_is_approved === 1;

    const spinnerHeight = useMemo(
        () => ({ height: `${window.innerHeight - 387}px` }),
        []
    );

    if (loading) {
        return (
            <OffcanvasTemplate
                open={show}
                handleClose={handleClose}
                placement="end"
                title="Approve for another date job"
                body={
                    <div style={spinnerHeight}>
                        <DivSpinner />
                    </div>
                }
            />
        );
    }

    return (
        <OffcanvasTemplate
            open={show}
            handleClose={handleClose}
            placement="end"
            title="Approve for another date job"
            body={
                <>
                    {/* Status Card */}
                    <div className="card">
                        <div className="card-body py-3">
                            <div className="d-flex">
                                <i
                                    className={`material-icons text-${isApproved ? "success" : "danger"}`}
                                    style={{ fontSize: 40 }}
                                >
                                    {isApproved ? "check_circle" : "cancel"} 
                                </i>

                                <div className="ms-2 my-auto">
                                    <h6 className="mb-0">
                                        {isApproved ? "Accept" : "Reject"}
                                    </h6>
                                    <p className="text-xs mb-0">
                                        #{anotherDateJob?.ftm_request?.ftm_request_id}
                                    </p>
                                </div>
                            </div>

                            <p className="mb-0 text-sm mt-2">
                                {anotherDateJob?.approve_for_another_date_job_description}
                            </p>
                        </div>

                        <hr className="dark horizontal my-0" />

                        <div className="card-footer d-flex justify-content-between align-items-center py-2">
                            <div className="d-flex align-items-center">
                                <i className="material-icons text-lg me-1">date_range</i>
                                <p className="text-sm my-auto">Created at</p>
                            </div>
                            <p className="font-weight-bold text-dark my-auto text-sm">
                                {formatDateInJobStatsLog(
                                    anotherDateJob?.approve_for_another_date_job_create_date
                                )}
                            </p>
                        </div>
                    </div>

                    {/* Job Info */}
                    <div className="card mt-5 mb-4">
                        <div className="card-body p-3">
                            <div className="d-flex mt-n2">
                                <div className="avatar avatar-xl bg-gradient-dark border-radius-xl p-2 mt-n4">
                                    <i className="material-icons" style={{ fontSize: 35 }}>
                                        {anotherDateJob?.job?.sub_skill?.skill?.skill_web_app_icon}
                                    </i>
                                </div>

                                <div className="ms-3 my-auto">
                                    <h6 className="mb-0">
                                        {anotherDateJob?.job?.sub_skill?.sub_skill_name}
                                    </h6>
                                    <p className="text-sm">
                                        {anotherDateJob?.job?.sub_skill?.skill?.skill_name}
                                    </p>
                                </div>
                            </div>

                            <p className="text-sm mt-1">
                                {anotherDateJob?.job?.job_description}
                            </p>

                            <hr className="horizontal dark" />

                            <div className="row">
                                <div className="col-6">
                                    <h6 className="text-sm mb-0">
                                        {anotherDateJob?.job?.job_postcode}
                                    </h6>
                                    <p className="text-secondary text-sm mb-0">
                                        Postcode
                                    </p>
                                </div>

                                <div className="col-6 text-end">
                                    <h6 className="text-sm mb-0">
                                        {anotherDateJob?.job?.job_createdAt}
                                    </h6>
                                    <p className="text-secondary text-sm mb-0">
                                        Job created at
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Request Details */}
                    <RequestDetailsCard
                        request={anotherDateJob?.ftm_request}
                    />

                    {/* Extra Materials */}
                    {anotherDateJob?.ftm_request?.request_materials?.map(
                        (material, index) => (
                            <ExtraMaterials key={index} material={material} />
                        )
                    )}

                    {/* Cost Breakdown */}
                    <CostBreakdown
                        total_material_net_price={
                            anotherDateJob?.ftm_request?.ftm_request_total_material_net_price
                        }
                    />
                </>
            }
            footer={null}
        />
    );
};

export default ApproveForAnotherDate;